//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useTranslation } from 'react-i18next';
import { BuildCard } from './cards/BuildCard';
import { PullRequestCard } from './cards/PullRequestCard';
import { CommitCard } from './cards/CommitCard';
import { DeploymentCard } from './cards/DeploymentCard';
import { IssueCard } from './cards/IssueCard';
import StoriesIcon from '../../icons/stories.svg';
import DefectIcon from '../../icons/DefectIcon.svg';
import StoryIcon from '../../icons/StoryIcon.svg';
import EpicIcon from '../../icons/EpicIcon.svg';
import TaskIcon from '../../icons/TaskIcon.svg';

// Re-export the canonical UnifiedCardData interface
import type { UnifiedCardData } from './cards/UnifiedCardData';
export type { UnifiedCardData } from './cards/UnifiedCardData';

interface FilterCardsProps {
  data: UnifiedCardData[];
  category: string;
}

const FilterCards: React.FC<FilterCardsProps> = ({ data, category }) => {
  const { t } = useTranslation();

  const renderCard = (card: UnifiedCardData) => {
    switch (card.type) {
      case 'Builds':
        return <BuildCard key={card.id} card={card} />;
      case 'Pull Requests':
        return <PullRequestCard key={card.id} card={card} />;
      case 'Commits':
        return <CommitCard key={card.id} card={card} />;
      case 'Deployments':
        return <DeploymentCard key={card.id} card={card} />;
      case 'Issues':
        // Choose icon based on issueType when available
        const issueTypeLower = (card.issueType || '').toLowerCase();
        const issueTypeIcon =
          issueTypeLower === 'epic'
            ? EpicIcon
            : issueTypeLower === 'story'
              ? StoryIcon
              : issueTypeLower === 'defect'
                ? DefectIcon
                : issueTypeLower === 'task'
                  ? TaskIcon
                  : StoriesIcon;
        return (
          <IssueCard
            key={card.id}
            card={card}
            iconSrc={issueTypeIcon}
            iconBg="#FEF3C7"
            typeLabel={t('Issues') || 'Issues'}
          />
        );
      default:
        return (
          <IssueCard
            key={card.id}
            card={card}
            iconSrc={StoriesIcon}
            iconBg="#E5E7EB"
            typeLabel={card.type}
          />
        );
    }
  };

  return (
    <div id="id-filter-cards-container" data-testid="filter-cards-container">
      {data.map(renderCard)}
    </div>
  );
};

export default FilterCards;
